import { useState, useEffect } from 'react'
import { User, Ticket } from '../types'
import { loadExecutors } from '../lib/api'
import { loadTickets } from '../lib/api'
import './ExecutorStatistics.css'

interface ExecutorStatisticsProps {
  onClose: () => void
}

interface ExecutorStats {
  executorId: string
  executorName: string
  total: number
  new: number
  inProgress: number
  completed: number
  completionRate: number
}

function ExecutorStatistics({ onClose }: ExecutorStatisticsProps) {
  const [executors, setExecutors] = useState<User[]>([])
  const [allTickets, setAllTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedExecutors, setSelectedExecutors] = useState<string[]>([]) // Пустой массив = все исполнители
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [stats, setStats] = useState<ExecutorStats[]>([])

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    calculateStats()
  }, [allTickets, selectedExecutors, dateFrom, dateTo])

  const loadData = async () => {
    try {
      setLoading(true)
      const [executorsData, ticketsData] = await Promise.all([
        loadExecutors(),
        loadTickets('', null, 'spravce_budov')
      ])
      setExecutors(executorsData)
      setAllTickets(ticketsData)
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
      alert('Chyba při načítání dat')
    } finally {
      setLoading(false)
    }
  }

  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([])

  const calculateStats = () => {
    let tickets = [...allTickets]

    // Фильтр по датам
    if (dateFrom) {
      const fromDate = new Date(dateFrom)
      fromDate.setHours(0, 0, 0, 0)
      tickets = tickets.filter(t => {
        const ticketDate = new Date(t.created_at)
        ticketDate.setHours(0, 0, 0, 0)
        return ticketDate >= fromDate
      })
    }

    if (dateTo) {
      const toDate = new Date(dateTo)
      toDate.setHours(23, 59, 59, 999)
      tickets = tickets.filter(t => {
        const ticketDate = new Date(t.created_at)
        return ticketDate <= toDate
      })
    }

    // Фильтр по исполнителям
    if (selectedExecutors.length > 0) {
      tickets = tickets.filter(t =>
        t.executor_id && selectedExecutors.includes(t.executor_id)
      )
    }

    // Сохраняем отфильтрованные заявки для общей статистики
    setFilteredTickets(tickets)

    // Группируем по исполнителям
    const statsMap = new Map<string, ExecutorStats>()

    // Инициализируем статистику для всех выбранных исполнителей
    const executorsToProcess = selectedExecutors.length > 0
      ? executors.filter(e => selectedExecutors.includes(e.id))
      : executors

    executorsToProcess.forEach(executor => {
      statsMap.set(executor.id, {
        executorId: executor.id,
        executorName: executor.name,
        total: 0,
        new: 0,
        inProgress: 0,
        completed: 0,
        completionRate: 0
      })
    })

    // Подсчитываем статистику
    tickets.forEach(ticket => {
      if (!ticket.executor_id) return

      const executorId = ticket.executor_id
      if (!statsMap.has(executorId)) {
        const executor = executors.find(e => e.id === executorId)
        if (executor) {
          statsMap.set(executorId, {
            executorId: executor.id,
            executorName: executor.name,
            total: 0,
            new: 0,
            inProgress: 0,
            completed: 0,
            completionRate: 0
          })
        } else {
          return
        }
      }

      const stat = statsMap.get(executorId)!
      stat.total++

      if (ticket.status === 'new') {
        stat.new++
      } else if (ticket.status === 'in_progress') {
        stat.inProgress++
      } else if (ticket.status === 'completed') {
        stat.completed++
      }
    })

    // Вычисляем процент выполнения
    statsMap.forEach(stat => {
      if (stat.total > 0) {
        stat.completionRate = Math.round((stat.completed / stat.total) * 100)
      }
    })

    // Сортируем по количеству заявок (убывание)
    const statsArray = Array.from(statsMap.values())
      .filter(stat => stat.total > 0) // Показываем только исполнителей с заявками
      .sort((a, b) => b.total - a.total)

    setStats(statsArray)
  }

  const handleExecutorToggle = (executorId: string) => {
    if (selectedExecutors.length === 0) {
      // Если выбраны все, переключаемся на конкретного
      setSelectedExecutors([executorId])
    } else if (selectedExecutors.includes(executorId)) {
      // Убираем из выбранных
      const newSelected = selectedExecutors.filter(id => id !== executorId)
      setSelectedExecutors(newSelected.length > 0 ? newSelected : [])
    } else {
      // Добавляем к выбранным
      setSelectedExecutors([...selectedExecutors, executorId])
    }
  }

  const handleSelectAll = () => {
    setSelectedExecutors([]) // Пустой массив = все исполнители
  }

  const handleClearSelection = () => {
    setSelectedExecutors([])
    setDateFrom('')
    setDateTo('')
  }

  // Устанавливаем даты по умолчанию (последние 30 дней)
  useEffect(() => {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)

    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0])
    setDateTo(today.toISOString().split('T')[0])
  }, [])

  if (loading) {
    return (
      <div className="executor-statistics-modal">
        <div className="executor-statistics-content">
          <div className="loading">Načítání...</div>
        </div>
      </div>
    )
  }

  // Считаем общую статистику напрямую из отфильтрованных заявок
  const totalStats = filteredTickets.reduce((acc, ticket) => {
    acc.total++
    if (ticket.status === 'new') acc.new++
    else if (ticket.status === 'in_progress') acc.inProgress++
    else if (ticket.status === 'completed') acc.completed++
    return acc
  }, { total: 0, new: 0, inProgress: 0, completed: 0 })

  const overallCompletionRate = totalStats.total > 0
    ? Math.round((totalStats.completed / totalStats.total) * 100)
    : 0

  return (
    <div className="executor-statistics-modal">
      <div className="executor-statistics-content">
        <div className="statistics-header">
          <h2>Statistika řešitelů</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="statistics-filters">
          <div className="filter-section">
            <h3>Řešitelé:</h3>
            <div className="executors-list">
              <label className="executor-checkbox">
                <input
                  type="checkbox"
                  checked={selectedExecutors.length === 0}
                  onChange={handleSelectAll}
                />
                <span>Všichni řešitelé</span>
              </label>
              {executors.map(executor => (
                <label key={executor.id} className="executor-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedExecutors.length === 0 || selectedExecutors.includes(executor.id)}
                    onChange={() => handleExecutorToggle(executor.id)}
                  />
                  <span>{executor.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Období:</h3>
            <div className="date-filters">
              <div className="date-input-group">
                <label>Od:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>Do:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="date-input"
                />
              </div>
              <button className="clear-filters-button" onClick={handleClearSelection}>
                Vymazat filtry
              </button>
            </div>
          </div>
        </div>

        {/* Celková statistika - показываем всегда */}
        <div className="overall-stats">
          <h3>Celková statistika</h3>
          <div className="overall-stats-grid">
            <div className="stat-card">
              <div className="stat-label">CELKEM ŽÁDOSTÍ</div>
              <div className="stat-value">{totalStats.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">NOVÉ</div>
              <div className="stat-value">{totalStats.new}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">V ŘEŠENÍ</div>
              <div className="stat-value">{totalStats.inProgress}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">DOKONČENO</div>
              <div className="stat-value">{totalStats.completed}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">ÚSPĚŠNOST</div>
              <div className="stat-value">{overallCompletionRate}%</div>
            </div>
          </div>
        </div>

        {/* Таблица по исполнителям */}
        {stats.length === 0 ? (
          <div className="no-stats">
            <p>Žádní řešitelé s žádostmi pro zadané filtry</p>
          </div>
        ) : (
          <div className="executors-stats-table">
            <h3>Statistika podle řešitele</h3>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Řešitel</th>
                  <th>Celkem</th>
                  <th>Nové</th>
                  <th>V řešení</th>
                  <th>Dokončeno</th>
                  <th>Úspěšnost</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(stat => (
                  <tr key={stat.executorId}>
                    <td className="executor-name">{stat.executorName}</td>
                    <td>{stat.total}</td>
                    <td>{stat.new}</td>
                    <td>{stat.inProgress}</td>
                    <td>{stat.completed}</td>
                    <td>
                      <div className="completion-rate">
                        <span className="rate-value">{stat.completionRate}%</span>
                        <div className="rate-bar">
                          <div
                            className="rate-bar-fill"
                            style={{ width: `${stat.completionRate}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExecutorStatistics
